import type { SocketType } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

/**
 * Generate the switch step's single input socket.
 *
 * `multiMatch` retypes it: an array socket so an upstream array feeds the
 * switch whole (every element tested against each case) instead of fanning out
 * one child run per element.
 */
export function generateSwitchInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const multiMatch = config.multiMatch === true;
  return [
    {
      id: "value",
      name: "Value",
      type: (multiMatch ? "array<json>" : "any") as SocketType,
      required: true,
      description: multiMatch
        ? 'Array of values — every case whose condition matches it (e.g. operator "contains") is taken'
        : "The value to evaluate against each case condition",
    },
  ];
}

/** Socket id of the switch step's fallback output. */
export const SWITCH_DEFAULT_SOCKET_ID = "default";

interface SwitchCaseSockets {
  /** The dynamic sockets, one per accepted case, in case order. */
  sockets: DynamicSocketDefinition[];
  /**
   * The socket id each configured case maps to, positionally, or `null` where
   * the case has no usable label or was refused as a duplicate.
   */
  socketIdByCaseIndex: Array<string | null>;
  hasDefault: boolean;
}

/**
 * Resolve a switch config into its case sockets and the id of each case.
 *
 * Both the generator and `switchHandler` go through this one call. A handler
 * receives `(input, config, context)` and never sees its sockets, so it has to
 * derive the same ids the canvas did; deriving them separately is how a handler
 * ends up writing `output[label]` for a socket the canvas calls
 * `label::dynamic`, which the engine then reads as undefined.
 */
export function resolveSwitchCaseSockets(
  config: Record<string, unknown>,
): SwitchCaseSockets {
  const cases = configEntryList<{ label: string }>(config.cases);
  const hasDefault = config.hasDefault !== false; // default true

  // Cases with no usable label are dropped, so track the original index to map
  // the results back onto `cases` positionally.
  const usable = cases
    .map((c, index) => ({ c, index }))
    .filter(({ c }) => typeof c.label === "string" && c.label.length > 0);

  const { sockets, assignedIds } = buildDynamicSockets({
    reserved: hasDefault ? [SWITCH_DEFAULT_SOCKET_ID] : [],
    version: effectiveSocketIdVersion("switch", config),
    // switchHandler resolves its payload keys through this same call, so a
    // legacy step whose case is labelled `default` can have the marked id
    // rather than losing the branch.
    markedFallback: supportsSocketIdV2("switch"),
    entries: usable.map(({ c }) => ({
      name: c.label,
      socket: {
        name: prettify(c.label),
        type: "any" as SocketType,
        required: false,
        description: `Output when "${c.label}" case matches`,
      },
    })),
  });

  const socketIdByCaseIndex: Array<string | null> = cases.map(() => null);
  usable.forEach(({ index }, position) => {
    socketIdByCaseIndex[index] = assignedIds[position];
  });

  return { sockets, socketIdByCaseIndex, hasDefault };
}

/**
 * Generate output sockets for the switch step from its configured cases.
 * Each case label becomes a conditional output socket, plus an optional default.
 *
 * With no cases configured, returns just the default socket (or empty if hasDefault is false).
 *
 * The default socket is appended last, matching the order every saved switch
 * node already has persisted.
 */
export function generateSwitchOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  const { sockets, hasDefault } = resolveSwitchCaseSockets(config);

  if (hasDefault) {
    sockets.push({
      id: SWITCH_DEFAULT_SOCKET_ID,
      name: "Default",
      type: "any" as SocketType,
      required: false,
      description: "Fallback output when no case matches",
    });
  }

  return sockets;
}
